import type { Text } from "@codemirror/state";
import { parseAssetRefPath } from "@magic-md/core";
import type {
    IntelligenceDocumentSymbol,
    SourceRange,
} from "@magic-md/engine-client/protocol";

export interface FlatDocumentSymbol {
    name: string;
    detail?: string;
    line: number;
    column: number;
    depth: number;
}

export function extractDefinitionIdAtCursor(
    lineText: string,
    column: number,
): string | null {
    const useFromTarget = extractUseFromTargetAtCursor(lineText, column);
    if (useFromTarget) return useFromTarget;

    const refTarget = extractRefTargetAtCursor(lineText, column);
    if (refTarget) return refTarget;

    const bracketPatterns = [
        /::define\[([^\]]+)\]/g,
        /::use\[([^\]]+)\]/g,
        /::content\[([^\]]+)\]/g,
        /::for\[([^\]]+)\]/g,
        /::render\[([^\]]+)\](?:\{[^}]*\})?::/g,
    ];

    for (const pattern of bracketPatterns) {
        let match = pattern.exec(lineText);
        while (match) {
            const fullMatch = match[0];
            const id = match[1];
            const fullStart = match.index;
            const fullEnd = fullStart + fullMatch.length;
            const idStart = match.index + fullMatch.indexOf("[") + 1;
            const idEnd = idStart + id.length;
            if (
                (column >= idStart && column <= idEnd) ||
                (column >= fullStart && column <= fullEnd)
            ) {
                return id;
            }
            match = pattern.exec(lineText);
        }
    }

    return null;
}

function extractRefTargetAtCursor(
    lineText: string,
    column: number,
): string | null {
    const refPattern = /\$ref\{([^}]+)\}/g;
    let refMatch = refPattern.exec(lineText);
    while (refMatch) {
        const fullMatch = refMatch[0];
        const refContent = refMatch[1];
        const refStart = refMatch.index;
        const refEnd = refStart + fullMatch.length;
        if (column >= refStart && column <= refEnd) {
            const assetRef = parseAssetRefPath(refContent);
            if (assetRef) {
                return assetRef.namespace ? `${assetRef.namespace}/assets` : "assets";
            }
            const targetId = refContent.split(".")[0] ?? "";
            if (targetId && !targetId.startsWith("$")) {
                return targetId;
            }
        }
        refMatch = refPattern.exec(lineText);
    }
    return null;
}

function extractUseFromTargetAtCursor(
    lineText: string,
    column: number,
): string | null {
    const usePattern = /::use\[[^\]]+\]\{([^}]+)\}/g;
    let useMatch = usePattern.exec(lineText);
    while (useMatch) {
        const attrs = useMatch[1];
        const attrsStart = useMatch.index + useMatch[0].indexOf("{") + 1;
        const fromPattern = /\bfrom="([^"]+)"/g;
        let fromMatch = fromPattern.exec(attrs);
        while (fromMatch) {
            const valueStart = attrsStart + fromMatch.index + 'from="'.length;
            const valueEnd = valueStart + fromMatch[1].length;
            if (column >= valueStart && column <= valueEnd) {
                return fromMatch[1];
            }
            fromMatch = fromPattern.exec(attrs);
        }
        useMatch = usePattern.exec(lineText);
    }
    return null;
}

export function flattenDocumentSymbols(
    symbols: IntelligenceDocumentSymbol[],
    depth = 0,
): FlatDocumentSymbol[] {
    const flattened: FlatDocumentSymbol[] = [];

    for (const symbol of symbols) {
        flattened.push({
            name: symbol.name,
            detail: symbol.detail,
            line: symbol.range.startLine,
            column: symbol.range.startColumn,
            depth,
        });
        if (symbol.children?.length) {
            flattened.push(...flattenDocumentSymbols(symbol.children, depth + 1));
        }
    }

    return flattened;
}

export function sourceRangeToOffsets(doc: Text, range: SourceRange): {
    from: number;
    to: number;
} | null {
    if (range.startLine < 1 || range.endLine < 1) return null;
    if (range.startLine > doc.lines || range.endLine > doc.lines) return null;

    const startLine = doc.line(range.startLine);
    const endLine = doc.line(range.endLine);
    const from = Math.min(startLine.from + range.startColumn, startLine.to);
    const to = Math.min(endLine.from + range.endColumn, endLine.to);
    return { from, to };
}

export function getAnchorCoords(view: { coordsAtPos(pos: number): { left: number; bottom: number } | null }, pos: number) {
    const coords = view.coordsAtPos(pos);
    return {
        x: coords?.left ?? 24,
        y: coords?.bottom ?? 24,
    };
}
